import React, { useEffect, useRef, useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaUploadQueue, { IKuikaUploadQueueItem } from "../../../../ui-kit/kuika-upload-queue/kuika-upload-queue";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

type DemoUploadKind = "progress-success" | "progress-fail-then-succeed" | "indeterminate-then-succeed";

interface IDemoUploadMeta {
  kind: DemoUploadKind;
  attempt: number;
  startedAt: number;
}

const TICK_MS: number = 200;
const PROGRESS_STEP: number = 0.12;
const FAIL_AT_PROGRESS: number = 0.6;
const INDETERMINATE_MS: number = 2000;

const STATE_ITEMS: IKuikaUploadQueueItem[] = [
  { id: "state-queued", name: "brief.pdf", sizeBytes: 1240000, status: "queued" },
  { id: "state-uploading-42", name: "diagram.png", sizeBytes: 860000, status: "uploading", progress: 0.42 },
  { id: "state-uploading-indeterminate", name: "archive.zip", sizeBytes: 18400000, status: "uploading" },
  { id: "state-success", name: "invoice.pdf", sizeBytes: 240000, status: "success" },
  {
    id: "state-error",
    name: "dataset.csv",
    sizeBytes: 5600000,
    status: "error",
    error: "Network connection lost"
  }
];

export const UploadQueueStory: React.FC = (): JSX.Element => {
  const [items, setItems] = useState<IKuikaUploadQueueItem[]>([]);
  const metaRef: React.MutableRefObject<Map<string, IDemoUploadMeta>> = useRef<Map<string, IDemoUploadMeta>>(new Map());
  const nextBatchRef: React.MutableRefObject<number> = useRef<number>(1);

  const handleRetry = (id: string): void => {
    const meta: IDemoUploadMeta | undefined = metaRef.current.get(id);
    if (meta) {
      metaRef.current.set(id, { ...meta, attempt: meta.attempt + 1, startedAt: Date.now() });
    }
    setItems((previous: IKuikaUploadQueueItem[]): IKuikaUploadQueueItem[] =>
      previous.map((queueItem: IKuikaUploadQueueItem): IKuikaUploadQueueItem =>
        queueItem.id === id ? { ...queueItem, status: "uploading", progress: 0, error: undefined } : queueItem
      )
    );
  };

  const handleRemove = (id: string): void => {
    metaRef.current.delete(id);
    setItems((previous: IKuikaUploadQueueItem[]): IKuikaUploadQueueItem[] =>
      previous.filter((queueItem: IKuikaUploadQueueItem): boolean => queueItem.id !== id)
    );
  };

  const handleAddFiles = (): void => {
    const batch: number = nextBatchRef.current;
    nextBatchRef.current += 1;
    const now: number = Date.now();

    const successId: string = `demo-${batch}-a`;
    const retryId: string = `demo-${batch}-b`;
    const indeterminateId: string = `demo-${batch}-c`;

    metaRef.current.set(successId, { kind: "progress-success", attempt: 1, startedAt: now });
    metaRef.current.set(retryId, { kind: "progress-fail-then-succeed", attempt: 1, startedAt: now });
    metaRef.current.set(indeterminateId, { kind: "indeterminate-then-succeed", attempt: 1, startedAt: now });

    const additions: IKuikaUploadQueueItem[] = [
      {
        id: successId,
        name: `Quarterly-Report-${batch}.pdf`,
        sizeBytes: 2400000,
        status: "uploading",
        progress: 0,
        onRetry: (): void => handleRetry(successId),
        onRemove: (): void => handleRemove(successId)
      },
      {
        id: retryId,
        name: `Customer-Export-${batch}.csv`,
        sizeBytes: 5100000,
        status: "uploading",
        progress: 0,
        onRetry: (): void => handleRetry(retryId),
        onRemove: (): void => handleRemove(retryId)
      },
      {
        id: indeterminateId,
        name: `Backup-Archive-${batch}.zip`,
        sizeBytes: 980000,
        status: "uploading",
        onRetry: (): void => handleRetry(indeterminateId),
        onRemove: (): void => handleRemove(indeterminateId)
      }
    ];

    setItems((previous: IKuikaUploadQueueItem[]): IKuikaUploadQueueItem[] => [...previous, ...additions]);
  };

  // A self-scheduling timeout, rather than a bare setInterval: it reschedules itself off the
  // latest `items` on every render, so the loop has nothing left to clean up (and does none) the
  // moment nothing is uploading, and never piles up ticks across a re-render. The effect's own
  // cleanup clears the pending tick on unmount or whenever `items` next changes.
  useEffect((): (() => void) | undefined => {
    const hasUploading: boolean = items.some(
      (queueItem: IKuikaUploadQueueItem): boolean => queueItem.status === "uploading"
    );
    if (!hasUploading) {
      return undefined;
    }

    const timeoutId: number = window.setTimeout((): void => {
      setItems((previous: IKuikaUploadQueueItem[]): IKuikaUploadQueueItem[] =>
        previous.map((queueItem: IKuikaUploadQueueItem): IKuikaUploadQueueItem => {
          if (queueItem.status !== "uploading") {
            return queueItem;
          }
          const meta: IDemoUploadMeta | undefined = metaRef.current.get(queueItem.id);
          if (!meta) {
            return queueItem;
          }

          if (meta.kind === "indeterminate-then-succeed") {
            const elapsedMs: number = Date.now() - meta.startedAt;
            return elapsedMs >= INDETERMINATE_MS ? { ...queueItem, status: "success" } : queueItem;
          }

          const nextProgress: number = Math.min(1, (queueItem.progress ?? 0) + PROGRESS_STEP);
          if (meta.kind === "progress-fail-then-succeed" && meta.attempt === 1 && nextProgress >= FAIL_AT_PROGRESS) {
            return { ...queueItem, status: "error", progress: undefined, error: "Network connection lost" };
          }
          if (nextProgress >= 1) {
            return { ...queueItem, status: "success", progress: 1 };
          }
          return { ...queueItem, progress: nextProgress };
        })
      );
    }, TICK_MS);

    return (): void => window.clearTimeout(timeoutId);
  }, [items]);

  return (
    <ShowcaseFrame
      title="Upload Queue"
      componentName="KuikaUploadQueue"
      sourcePath="ui-kit/kuika-upload-queue"
      description="A list of file uploads whose rows arrive, progress, retry and collapse away with the standard row-entrance and shake-on-error motion."
    >
      <SpecRow
        label="Live"
        caption="add three files — one succeeds, one fails partway and retries clean, one runs indeterminate — remove any row once it settles"
      >
        <KuikaButton type="secondary" size="sm" onClick={handleAddFiles}>
          Add files
        </KuikaButton>
        <div style={{ width: 420 }}>
          <KuikaUploadQueue items={items} data-testid="upload-queue-live" />
        </div>
      </SpecRow>
      <SpecRow label="States" caption="queued · uploading (42%) · uploading (indeterminate) · success · error">
        <div style={{ width: 420 }}>
          <KuikaUploadQueue items={STATE_ITEMS} data-testid="upload-queue-states" />
        </div>
      </SpecRow>
    </ShowcaseFrame>
  );
};
